import { useState } from 'react';
import { Paginas } from './Paginas';
import { Platforms } from './Platforms';
import { Ventas } from './Ventas';
import { useT } from '../i18n';

/**
 * La sección «Estadísticas» del menú: todo lo que se mide, en un solo sitio.
 *
 * ── Por qué las tres juntas ───────────────────────────────────────────────────
 * Porque son tres tramos del mismo embudo y estaban repartidas por el menú: las páginas dicen
 * cuánta gente llega y cuánta sigue hasta una red, las plataformas dicen cómo le va a cada una
 * donde publica, y las ventas dicen cuánta de esa gente pagó. Separadas, leer el recorrido
 * completo era recorrer el menú entero y acordarse de dónde estaba cada parte.
 *
 * ── Por qué ninguna de las tres está dentro de una influencer ─────────────────
 * Porque la pregunta que contestan es comparativa: «a cuál le va mejor», «qué vende». Eso
 * necesita verlas juntas. Cada panel trae su propio selector para bajar a UNA cuando hace falta,
 * que es el mismo patrón que la pantalla de Páginas ya usaba desde antes.
 *
 * ── Por qué se llama así y no `Estadisticas` ──────────────────────────────────
 * Porque ese nombre ya es el de la pantalla de UNA influencer —`/influencer/:id/estadisticas`,
 * la de sus métricas sociales— y son cosas distintas. Dos componentes con el mismo nombre en el
 * mismo directorio se confunden al importar, y el que se equivoque no lo va a ver hasta
 * ejecutarlo.
 */
export function EstadisticasGenerales() {
  const { t } = useT();
  const [panel, setPanel] = useState<'paginas' | 'plataformas' | 'ventas'>('paginas');

  return (
    <div className="space-y-4">
      <div
        className="flex flex-wrap gap-1 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-1"
        role="tablist"
        aria-label={t('nav.estadisticas')}
      >
        {(
          [
            ['paginas', t('tab.paginas')],
            ['plataformas', t('tab.plataformas')],
            ['ventas', t('tab.ventas')],
          ] as const
        ).map(([clave, etiqueta]) => (
          <button
            key={clave}
            type="button"
            role="tab"
            aria-selected={panel === clave}
            onClick={() => setPanel(clave)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              panel === clave
                ? 'bg-[var(--color-surface-raised)] text-[var(--color-ink)] shadow-sm'
                : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {/*
        Los tres son las pantallas que ya existían, sin tocar.

        Páginas y Plataformas se movieron del menú a acá y siguen siendo el mismo componente: no
        se copiaron ni se adaptaron, porque una copia es una que un día deja de coincidir con la
        otra. Ventas es la única nueva, y se escribió con la misma forma que Páginas para que las
        tres se lean igual.
      */}
      {panel === 'paginas' && <Paginas />}
      {panel === 'plataformas' && <Platforms />}
      {panel === 'ventas' && <Ventas />}
    </div>
  );
}
